/** @file beginQuadEdit: starts a resize or pivot drag on the selected part's quad handle under the pointer. */
import { cssToTarget } from './cssToTarget';
import { docEntryOf } from './docEntryOf';
import { QUAD_HANDLE_METRICS } from './quadHandleMetrics';
import { quadHandleAt } from './quadHandleAt';
import { selectedQuad } from './selectedQuad';
import { shownTileOf } from './shownTileOf';
import type { ViewportCtx } from './viewportCtx';

/**
 * Left button down with the quads view on: the handle of `selectedQuad` under `p` (target px) grabs the entry the
 * viewport draws for that part (`shownTileOf`) and sets `ctx.quad.edit` (true). False when no handle is under the
 * pointer (the caller goes on to the gizmo and selection), or while a released drag still waits for its batch.
 */
export function beginQuadEdit(ctx: ViewportCtx, p: [number, number]): boolean {
  if (ctx.quad.edit !== null) return ctx.quad.edit.pending;
  const q = selectedQuad(ctx);
  const handle = q === null ? null : quadHandleAt(q.points, p, QUAD_HANDLE_METRICS.grab * cssToTarget(ctx));
  if (q === null || handle === null) return false;
  const ref = shownTileOf(ctx, q.drawableIndex);
  const entry = ref === null ? null : docEntryOf(ctx.store, ref);
  if (ref === null || entry === null) return false;
  const [w, h] = entry.size;
  const pivot: [number, number] = [entry.pivotFrac[0] * w, entry.pivotFrac[1] * h];
  ctx.quad.edit = {
    drawable: ref.drawable, drawableIndex: q.drawableIndex, ref, handle, start: p, u: q.geom.u, v: q.geom.v, flip: q.geom.flip,
    size0: [w, h], pivot0: pivot, size: [w, h], pivot: [pivot[0], pivot[1]], moved: false, pending: false,
  };
  return true;
}
